// src/components/payrolls/AddStaffModal.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import {
  X,
  User,
  Briefcase,
  DollarSign,
  Info,
  Save,
  Calendar,
} from "lucide-react";
import { ExclamationCircleIcon as HeroExclamationCircleIcon } from "@heroicons/react/24/outline";
import { createPayroll } from "../../services/payroll.service";

// Re-usable react-select style
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "0.75rem",
    minHeight: "44px",
    paddingLeft: "42px",
    borderColor: state.isFocused ? "#F97316" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 1px #F97316" : "none",
    "&:hover": { borderColor: "#F97316" },
  }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
  singleValue: (base) => ({ ...base, color: "#1F2937" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F97316" : state.isFocused ? "#FFEDD5" : "white",
    color: state.isSelected ? "white" : "#1F2937",
    "&:hover": { backgroundColor: "#FFEDD5", color: "#1F2937" },
  }),
  dropdownIndicator: (base) => ({ ...base, color: "#6B7280", "&:hover": { color: "#F97316" } }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "#D1D5DB" }),
  menu: (base) => ({ ...base, borderRadius: "0.75rem", border: "1px solid #E5E7EB" }),
};

const Section = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border rounded-2xl mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-6 py-3 bg-gradient-to-r from-orange-50 to-orange-50 font-semibold text-gray-700 flex justify-between items-center"
      >
        <span className="text-sm text-gray-800">{title}</span>
        <span className="text-xl text-orange-500">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>}
    </div>
  );
};

function FileIconLike() {
  return (
    <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6M7 7h8l2 2v8a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2z" />
    </svg>
  );
}

export default function AddStaffModal({ isOpen, onClose, onAdded }) {
  const modalRef = useRef(null);

  // Initialize Month/Year with current date by default
  const currentDate = new Date();
  
  const [formData, setFormData] = useState({
    staff: "",
    department: "",
    job: "",
    grossSalary: "",
    deductible: "",
    hardship: "",
    medical: "",
    arrears: "",
    nsiPercentage: "",
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
    projectId: null, // --- NEW CHANGE: Add projectId to state
  });

  const [staffList, setStaffList] = useState([]);
  const [projects, setProjects] = useState([]); // --- NEW CHANGE: State for projects ---
  const [status, setStatus] = useState({ error: "", success: "", loading: false });
  const [inputError, setInputError] = useState("");

  // --- Helpers for Dropdowns ---
  const months = Array.from({length: 12}, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString('default', { month: 'long' })
  }));

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear + 1].map(y => ({ value: y, label: y.toString() }));

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const parseMoney = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    return Number(value.toString().replace(/,/g, "")) || 0;
  };

  // --- MODIFIED CHANGE: Fetch both Staff and Projects ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        
        // Fetch Staff
        const staffRes = await axios.get(
          "https://ims.holdthechild.net/api/staff",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStaffList(staffRes.data.data || []);

        // --- NEW CHANGE: Fetch Projects ---
        const projRes = await axios.get(
          "https://ims.holdthechild.net/api/projects",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProjects(projRes.data.data || []);

      } catch (err) {
        console.error("Error fetching data:", err);
        setStatus({ ...status, error: "Failed to load data." });
      }
    };
    if (isOpen) fetchData();
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleNestedChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStaffSelect = (selected) => {
    if (!selected) {
      setFormData({
        staff: "",
        department: "",
        job: "",
        grossSalary: "",
        deductible: "",
        hardship: "",
        medical: "",
        arrears: "",
        nsiPercentage: "",
        month: formData.month,
        year: formData.year,
        projectId: null, // --- NEW CHANGE: Reset projectId when staff changes
      });
      setInputError("");
      return;
    }

    const formattedSalary = formatCurrency(selected.salary || 0);

    setFormData((prev) => ({
      ...prev,
      staff: selected.value,
      department: selected.department || "",
      job: selected.role || "",
      grossSalary: formattedSalary,
      medical: formatCurrency(selected.medicalAllowance || 0),
      deductible: "",
      hardship: "",
      arrears: "",
      nsiPercentage: selected.nsiPercentage !== undefined ? selected.nsiPercentage.toString() : "", 
    }));
    setInputError("");
  };

  const handleMoneyChange = (field) => (e) => {
    let rawValue = e.target.value.replace(/[^0-9]/g, '');
    
    if (rawValue === '') {
      handleNestedChange(field, '');
      if (field === 'deductible') setInputError("");
      return;
    }

    const numericValue = Number(rawValue);
    const formattedValue = formatCurrency(numericValue);

    if (field === 'deductible') {
      const grossNum = parseMoney(formData.grossSalary);
      
      if (grossNum > 0 && numericValue > grossNum) {
        setInputError("Deductible amount cannot exceed Gross Salary.");
      } else {
        setInputError("");
      }
    }

    handleNestedChange(field, formattedValue);
  };

  const handleNSIChange = (e) => {
    let rawValue = e.target.value.replace(/[^0-9]/g, '');
    handleNestedChange('nsiPercentage', rawValue);
  }

  const handleSubmit = async () => {
    if (!formData.staff) {
      setStatus({ ...status, error: "Please select a staff member." });
      return;
    }

    if (inputError) {
      setStatus({ ...status, error: inputError });
      return;
    }

    setStatus({ error: "", success: "", loading: true });
    try {
      await createPayroll({
        staffId: formData.staff,
        deductibleAmount: parseMoney(formData.deductible),
        hardship: parseMoney(formData.hardship),
        arrears: parseMoney(formData.arrears),
        medicalAllocated: parseMoney(formData.medical),
        nsiPercentage: Number(formData.nsiPercentage || 0),
        month: Number(formData.month),
        year: Number(formData.year),
        projectId: formData.projectId, // --- NEW CHANGE: Send projectId ---
      });

      setStatus({ error: "", success: "Staff added successfully!", loading: false });
      if (onAdded) onAdded();
      onClose();
    } catch (err) {
      setStatus({
        error: err.message || "Failed to add staff to payroll.",
        success: "",
        loading: false,
      });
    }
  };

  if (!isOpen) return null;

  const staffOptions = staffList.map((staff) => ({
    value: staff._id,
    label: `${staff.firstName} ${staff.lastName} — ${staff.jobTitle || staff.role || "No Role"}`,
    department: staff.department || "",
    role: staff.jobTitle || staff.role || "", 
    salary: staff.salaryRange || 0, 
    medicalAllowance: staff.medicalAllowance || 0,
    nsiPercentage: staff.nsiPercentage || 0,
  }));

  // --- NEW CHANGE: Map Projects for Select Dropdown ---
  const projectOptions = projects.map(p => ({
    value: p._id,
    label: `${p.budgetCode || 'N/A'} - ${p.projectTitle}`
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <User className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Add Staff to Payroll</h2>
                <p className="text-lg text-black-100 mt-1">Assign a staff member to this payroll</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-grow px-8 py-6 overflow-y-auto">
          {status.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <HeroExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Error</p>
                  <p className="mt-1">{status.error}</p>
                </div>
              </div>
            </div>
          )}

          {inputError && !status.error && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl animate-pulse">
              <div className="flex items-center space-x-2 text-sm text-yellow-800">
                 <Info className="w-4 h-4" />
                 <span>{inputError}</span>
              </div>
            </div>
          )}

          {status.success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm text-green-700">
                  <p className="font-medium">Success</p>
                  <p className="mt-1">{status.success}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            
            {/* Payroll Period Section */}
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-4">
               <h3 className="text-sm font-bold text-orange-800 mb-3 flex items-center gap-2">
                 <Calendar className="w-4 h-4" /> Payroll Period
               </h3>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Month</label>
                    <div className="relative">
                       <select 
                          value={formData.month} 
                          onChange={(e) => handleNestedChange('month', e.target.value)}
                          className="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white appearance-none"
                       >
                          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                       </select>
                       <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                       </div>
                    </div>
                 </div>
                 <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Year</label>
                    <div className="relative">
                       <select 
                          value={formData.year} 
                          onChange={(e) => handleNestedChange('year', e.target.value)}
                          className="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white appearance-none"
                       >
                          {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                       </select>
                       <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                       </div>
                    </div>
                 </div>
               </div>
            </div>

            {/* --- NEW CHANGE: Budget Allocation Section --- */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
               <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                 <Briefcase className="w-4 h-4" /> Budget Allocation
               </h3>
               <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Select Project / Budget Code</label>
                  <div className="relative">
                     <Select
                        options={projectOptions}
                        onChange={(selected) => handleNestedChange('projectId', selected ? selected.value : null)}
                        value={projectOptions.find(opt => opt.value === formData.projectId) || null}
                        placeholder="Search for a project..."
                        isClearable
                        styles={customSelectStyles}
                        className="w-full rounded-xl"
                     />
                     <p className="text-xs text-gray-500 mt-1">
                        Selecting a project links this salary to its budget code.
                     </p>
                  </div>
               </div>
            </div>

            <Section title="Staff Details">
              <div className="col-span-2">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Staff</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Required</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <Select
                    options={staffOptions}
                    onChange={handleStaffSelect}
                    value={staffOptions.find((opt) => opt.value === formData.staff) || null}
                    placeholder="Search and select staff..."
                    styles={customSelectStyles}
                    className="w-full rounded-xl"
                  />
                </div>
              </div>

              <label>
                Department
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase className="h-5 w-5 text-gray-400" />
                  </div>
                  <input type="text" name="department" value={formData.department} readOnly className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none" />
                </div>
              </label>

              <label>
                Job / Role
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileIconLike />
                  </div>
                  <input type="text" name="job" value={formData.job} readOnly className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none capitalize" />
                </div>
              </label>
            </Section>

            <Section title="Salary Details">
              {["grossSalary", "deductible", "hardship", "medical", "arrears"].map((field, idx) => {
                const labelMap = {
                  grossSalary: "Gross Salary",
                  deductible: "Deductible Amount",
                  hardship: "Hardship",
                  medical: "Medical Allocated",
                  arrears: "Arrears",
                };
                const readOnly = field === "grossSalary";
                const isDeductible = field === "deductible";
                const borderClass = (isDeductible && inputError) ? "border-red-500" : "border-gray-300";

                return (
                  <label key={idx}>
                    {labelMap[field]}
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formData[field]}
                        onChange={readOnly ? undefined : handleMoneyChange(field)}
                        readOnly={readOnly}
                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${borderClass} ${readOnly ? "bg-gray-100" : "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"}`}
                      />
                    </div>
                  </label>
                );
              })}

              <label>
                NSI Percentage
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                   <span className="text-gray-400 text-sm">%</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.nsiPercentage}
                    onChange={handleNSIChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </label>
            </Section>

            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <Info className="w-4 h-4 text-orange-500" />
                <span>All fields marked as required must be completed</span>
              </div>

              <div className="flex items-center space-x-4">
                <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200" disabled={status.loading}>
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={status.loading || !!inputError}
                  className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${status.loading || inputError ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl"}`}
                >
                  {status.loading ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Adding...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="w-5 h-5" />
                      <span>Add</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}